import type { UseStore } from 'idb-keyval'
import { createStore, del, get, set, values } from 'idb-keyval'
import type { Workspace, WorkspaceSummary } from '../../Flux/Slice/Chat/Workspace.js'
import { migrateWorkspace } from '../../Flux/Slice/Chat/Workspace.js'
import type { PayloadCodec } from './PayloadCodec.js'
import { NoopJsonCodec } from './PayloadCodec.js'

/**
 * W-0 — the persistence adapter behind which "browser-only now, server later" is a drop-in. The feature code
 * (Save/Resume UI) talks only to this interface; W-3 adds a `ServerWorkspaceStore` (POST/GET opaque blobs)
 * with no call-site change. Every impl persists the CODEC-ENCODED blob (see {@link PayloadCodec}) — the store
 * only ever holds opaque strings, so encryption is a codec swap, not a store rewrite.
 */
export interface WorkspaceStore {
  save(ws: Workspace): Promise<void>
  /** Summaries for one scope, newest-first. */
  list(scopeKey: string): Promise<WorkspaceSummary[]>
  get(id: string): Promise<Workspace | null>
  delete(id: string): Promise<void>
}

function toSummary(ws: Workspace): WorkspaceSummary {
  return { id: ws.id, title: ws.title, scopeKey: ws.scopeKey, savedAt: ws.savedAt }
}

/** Newest-first by `savedAt` (ISO-8601 sorts lexicographically). */
function bySavedAtDesc(a: WorkspaceSummary, b: WorkspaceSummary): number {
  return b.savedAt.localeCompare(a.savedAt)
}

/**
 * The real browser impl (IndexedDB via `idb-keyval`). Each workspace is stored as its opaque codec blob keyed
 * by `id` in a dedicated object store. `list` decodes the blobs and filters by `scopeKey` client-side — the
 * store holds only opaque blobs (no queryable cleartext index), which is exactly what keeps a future encrypting
 * codec a drop-in (an encrypted blob is not scope-queryable; at W-0 scale a handful of workspaces this is cheap).
 */
export class IndexedDbWorkspaceStore implements WorkspaceStore {
  // `createStore` calls `indexedDB.open(...)` synchronously; build it lazily so importing this module in a
  // non-browser test env (jsdom without indexedDB) doesn't crash unless the store is actually exercised.
  private cached?: UseStore

  constructor(private readonly codec: PayloadCodec = NoopJsonCodec) {}

  private store(): UseStore {
    if (!this.cached) this.cached = createStore('spcfy-chat-workspaces', 'workspaces')
    return this.cached
  }

  async save(ws: Workspace): Promise<void> {
    await set(ws.id, await this.codec.encode(ws), this.store())
  }

  async get(id: string): Promise<Workspace | null> {
    const blob = await get<string>(id, this.store())
    if (blob === undefined) return null
    return migrateWorkspace((await this.codec.decode(blob)) as Workspace)
  }

  async delete(id: string): Promise<void> {
    await del(id, this.store())
  }

  async list(scopeKey: string): Promise<WorkspaceSummary[]> {
    const blobs = await values<string>(this.store())
    const out: WorkspaceSummary[] = []
    for (const blob of blobs) {
      const ws = (await this.codec.decode(blob)) as Workspace
      if (ws.scopeKey === scopeKey) out.push(toSummary(ws))
    }
    return out.sort(bySavedAtDesc)
  }
}

/** In-memory impl for tests (same codec seam + scopeKey filtering as the real store). */
export class InMemoryWorkspaceStore implements WorkspaceStore {
  private readonly blobs = new Map<string, string>()

  constructor(private readonly codec: PayloadCodec = NoopJsonCodec) {}

  async save(ws: Workspace): Promise<void> {
    this.blobs.set(ws.id, await this.codec.encode(ws))
  }

  async get(id: string): Promise<Workspace | null> {
    const blob = this.blobs.get(id)
    if (blob === undefined) return null
    return migrateWorkspace((await this.codec.decode(blob)) as Workspace)
  }

  async delete(id: string): Promise<void> {
    this.blobs.delete(id)
  }

  async list(scopeKey: string): Promise<WorkspaceSummary[]> {
    const out: WorkspaceSummary[] = []
    for (const blob of this.blobs.values()) {
      const ws = (await this.codec.decode(blob)) as Workspace
      if (ws.scopeKey === scopeKey) out.push(toSummary(ws))
    }
    return out.sort(bySavedAtDesc)
  }
}

/** The app-wide default: browser IndexedDB store with the no-op JSON codec (W-0). */
export const defaultWorkspaceStore: WorkspaceStore = new IndexedDbWorkspaceStore()
